// Everything the crew does to a call or to money from the tablet (STAGE_5 5.3-5.6).
//
// One road for all of them: with a network the action goes straight out, carrying an id
// of its own so a retry cannot do it twice; without one it is written to the offline
// queue and goes out in order when the network is back (offline/queue.ts). The screens
// get back either the server's answer or "queued", and say so.

import { NetworkError, api, type Schemas } from '@/api/client';
import { ru } from '@/i18n/ru';


import { enqueue, flush, type NewAction } from './offline/queue';

export type CrewStatus = 'en_route' | 'on_scene' | 'transporting' | 'completed';

/** What a screen gets back: the server's answer, or the note that it is waiting. */
export type Sent<T> = { queued: false; result: T } | { queued: true };

/** A photo that is itself still in the queue is referred to by its place in it. */
const QUEUED_TOKEN = 'queued:';
export const queuedToken = (seq: number) => `${QUEUED_TOKEN}${String(seq)}`;
export const isQueuedToken = (token: string | null | undefined): boolean =>
  typeof token === 'string' && token.startsWith(QUEUED_TOKEN);
const tokenSeq = (token: string) => Number(token.slice(QUEUED_TOKEN.length));

interface Call<T> {
  label: string;
  method: 'post' | 'put';
  path: string;
  pathParams?: Record<string, string | number>;
  body?: Record<string, unknown>;
  /** The field of the body that carries a stored file's token. */
  tokenField?: string;
  /** The queued photos an incident waits for, and the field that holds them (TZ 9). */
  photosFrom?: number[];
  photosField?: string;
  send: (actionId: string) => Promise<T>;
}

async function submit<T>(call: Call<T>): Promise<Sent<T>> {
  const id = crypto.randomUUID();
  const queued = (): NewAction => {
    const given = call.body ?? {};
    const field = call.tokenField;
    const waitsFor = field && isQueuedToken(given[field] as string) ? tokenSeq(given[field] as string) : undefined;
    // A photo still in the queue is not a token yet: the field goes out with it.
    const body = Object.fromEntries(
      Object.entries(given).filter(([key]) => !(waitsFor !== undefined && key === field)),
    );
    return {
      id,
      method: call.method,
      path: call.path,
      ...(call.pathParams ? { pathParams: call.pathParams } : {}),
      body,
      ...(waitsFor === undefined ? {} : { tokenFrom: waitsFor, tokenField: field }),
      ...(call.photosFrom?.length
        ? { photosFrom: call.photosFrom, photosField: call.photosField }
        : {}),
      label: call.label,
    };
  };

  if (!navigator.onLine) {
    await enqueue(queued());
    return { queued: true };
  }
  try {
    const result = await call.send(id);
    // Anything that was waiting can go now too.
    void flush();
    return { queued: false, result };
  } catch (error) {
    if (!(error instanceof NetworkError)) throw error;
    await enqueue(queued());
    return { queued: true };
  }
}

const k = ru.crew.calls;
const c = ru.crew;

export const crewActions = {
  accept: (callId: number, seq: number) =>
    submit({
      label: k.accept,
      method: 'post',
      path: '/calls/{call_id}/kv/{seq}/accept',
      pathParams: { call_id: callId, seq },
      send: (actionId) =>
        api.post('/calls/{call_id}/kv/{seq}/accept', {
          path: { call_id: callId, seq },
          actionId,
        }),
    }),

  /** `confirmZero`: complete although there is nothing to pay, the doctor having said so. */
  move: (callId: number, toStatus: CrewStatus, confirmZero = false) => {
    const body = { to_status: toStatus, confirm_zero: confirmZero };
    return submit({
      label: c.calls.done[toStatus],
      method: 'post',
      path: '/calls/{call_id}/transition',
      pathParams: { call_id: callId },
      body,
      send: (actionId) =>
        api.post('/calls/{call_id}/transition', {
          path: { call_id: callId },
          body,
          actionId,
        }),
    });
  },

  waiting: (callId: number, start: boolean) =>
    submit({
      label: start ? k.waitingStarted : k.waitingEnded,
      method: 'post',
      path: '/calls/{call_id}/events',
      pathParams: { call_id: callId },
      body: { event_type: start ? 'waiting_started' : 'waiting_ended' },
      send: (actionId) =>
        api.post('/calls/{call_id}/events', {
          path: { call_id: callId },
          body: { event_type: start ? 'waiting_started' : 'waiting_ended' },
          actionId,
        }),
    }),

  changeDestination: (callId: number, to: { hospitalId: number } | { address: string }) => {
    const where =
      'hospitalId' in to ? { dest_hospital_id: to.hospitalId } : { dest_address: to.address };
    const body = { event_type: 'destination_changed' as const, ...where };
    return submit({
      label: k.changeDestination,
      method: 'post',
      path: '/calls/{call_id}/events',
      pathParams: { call_id: callId },
      body,
      send: (actionId) =>
        api.post('/calls/{call_id}/events', {
          path: { call_id: callId },
          body,
          actionId,
        }),
    });
  },

  /** "Problem": an incident of its own, with the consequences its type carries (TZ 9).
   *
   * Photos taken without a network are in the queue themselves; the incident goes out
   * after them and picks up their tokens (offline/queue.ts). */
  reportProblem: (callId: number, typeId: number, description: string, photos: string[] = []) => {
    const sent = photos.filter((token) => !isQueuedToken(token));
    const waiting = photos.filter(isQueuedToken).map((token) => Number(token.split(':')[1]));
    const body = {
      type_id: typeId,
      call_id: callId,
      description,
      severity: 'medium' as const,
      photos: sent,
    };
    return submit({
      label: k.problem,
      method: 'post',
      path: '/incidents',
      body,
      ...(waiting.length > 0 ? { photosFrom: waiting, photosField: 'photos' } : {}),
      send: (actionId) => api.post('/incidents', { body: { ...body, photos }, actionId }),
    });
  },

  /** Save the call card as it stands (a draft at every step). */
  saveReport: (callId: number, seq: number, body: Schemas['CallReportIn']) =>
    submit({
      label: ru.crew.report.open,
      method: 'put',
      path: '/calls/{call_id}/kv/{seq}/report',
      pathParams: { call_id: callId, seq },
      body,
      tokenField: 'refusal_signature_ref',
      send: (actionId) =>
        api.put('/calls/{call_id}/kv/{seq}/report', {
          path: { call_id: callId, seq },
          body,
          actionId,
        }),
    }),

  /** Take money for a call (TZ 11): cash and terminal carry the fiscal receipt number. */
  pay: (callId: number, body: Schemas['PaymentCreate']) =>
    submit({
      label: ru.crew.payment.accept,
      method: 'post',
      path: '/calls/{call_id}/payments',
      pathParams: { call_id: callId },
      body,
      tokenField: 'receipt_photo_ref',
      send: (actionId) =>
        api.post('/calls/{call_id}/payments', { path: { call_id: callId }, body, actionId }),
    }),

  /** Enter a spend against the crew's own open shift (TZ 11.1). */
  spend: (body: Schemas['ExpenseCreate']) =>
    submit({
      label: ru.crew.expense.save,
      method: 'post',
      path: '/expenses',
      body,
      tokenField: 'receipt_photo_ref',
      send: (actionId) => api.post('/expenses', { body, actionId }),
    }),
};
